import Link from "@whoppah/next/link";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { Price } from "@/Apollo/schema";
import { useFormatPrice } from "@/common/hooks/intl.hooks";
import { ACCOUNT_ADS_ROUTE } from "@/common/static/routes.static";
import { TradeDiscountAdLabel } from "@/features/pro-buyer-discount/components/TradeDiscountAdMarks";
import { useTradeDiscount } from "@/features/pro-buyer-discount/hooks/useTradeDiscount";

type TradeDiscountOwnerLineProps = {
  /** `product.isOwner` from the PDP query; the line is owner-only. */
  isOwner: boolean | null | undefined;
  /**
   * `product.tradeDiscountPreviewPrice`: the gateway returns it only to the
   * owner, only while the discount is on and only for an eligible listing.
   */
  previewPrice: Pick<Price, "amount" | "currency"> | null | undefined;
};

/**
 * WHO-5654, own PDP: "Pro Buyers see € 1.572,- · adjust discount" under the
 * price. Renders only when the gateway returned a preview price, so the
 * eligibility rules live in the gateway alone. The discount itself is only
 * requested for the owner (for the label's percentage).
 */
export const TradeDiscountOwnerLine = ({
  isOwner,
  previewPrice,
}: TradeDiscountOwnerLineProps) => {
  const { t } = useTranslation();
  const { locale = "en" } = useRouter();
  const { formatPrice } = useFormatPrice({
    locale,
    currency: previewPrice?.currency,
  });
  const { activePct } = useTradeDiscount({
    enabled: isOwner === true && previewPrice != null,
  });

  if (!isOwner || !previewPrice || activePct === null) {
    return null;
  }

  return (
    <span
      className="flex flex-col items-start gap-1 font-body text-sm text-brand-600"
      data-testid="pro-buyer-discount-owner-line"
    >
      <TradeDiscountAdLabel pct={activePct} />
      <span>
        {t("pdp:pdp-pro-buyer-discount-owner", {
          defaultValue: "Pro Buyers see {{price}}",
          price: formatPrice(previewPrice.amount),
        })}
        {" · "}
        <Link href={ACCOUNT_ADS_ROUTE} className="underline">
          {t("pdp:pdp-pro-buyer-discount-adjust", "adjust discount")}
        </Link>
      </span>
    </span>
  );
};
